/**
 * Adaptive quality controller.
 *
 * Monitors a rolling FPS average and adjusts render resolution scale
 * to maintain a target frame rate.
 *
 * Scale steps down when FPS drops below `lowFpsThreshold` for longer
 * than `lowFpsDuration` seconds. Steps back up when FPS exceeds
 * `highFpsThreshold` for longer than `highFpsDuration` seconds.
 */
const DEFAULT_OPTIONS = {
    lowFpsThreshold: 30,
    lowFpsDuration: 2,
    highFpsThreshold: 45,
    highFpsDuration: 5,
    minScale: 0.5,
    maxScale: 1.0,
    scaleStep: 0.25,
    fpsSmoothing: 0.1,
};
export class AdaptiveQuality {
    _scale;
    _fpsAvg = 0;
    _lowTime = 0;
    _highTime = 0;
    _opts;
    /** Called when scale changes: (newScale, oldScale) */
    onScaleChange = null;
    constructor(options = {}) {
        this._opts = { ...DEFAULT_OPTIONS, ...options };
        this._scale = this._opts.maxScale;
    }
    /** Current resolution scale (0.5 – 1.0) */
    get scale() {
        return this._scale;
    }
    /** Current smoothed FPS average */
    get currentFps() {
        return this._fpsAvg;
    }
    /** True when rendering at reduced resolution */
    get isLowQuality() {
        return this._scale < this._opts.maxScale;
    }
    /**
     * Feed a frame delta time (seconds). Call once per rendered frame.
     */
    frame(dt) {
        if (dt <= 0)
            return;
        const instantFps = 1 / dt;
        this._fpsAvg =
            this._fpsAvg === 0
                ? instantFps
                : this._fpsAvg * (1 - this._opts.fpsSmoothing) +
                    instantFps * this._opts.fpsSmoothing;
        if (this._fpsAvg < this._opts.lowFpsThreshold) {
            this._lowTime += dt;
            this._highTime = 0;
            if (this._lowTime >= this._opts.lowFpsDuration) {
                this._stepDown();
                this._lowTime = 0;
            }
        }
        else if (this._fpsAvg > this._opts.highFpsThreshold) {
            this._highTime += dt;
            this._lowTime = 0;
            if (this._highTime >= this._opts.highFpsDuration) {
                this._stepUp();
                this._highTime = 0;
            }
        }
        else {
            this._lowTime = 0;
            this._highTime = 0;
        }
    }
    _stepDown() {
        const oldScale = this._scale;
        this._scale = Math.max(this._opts.minScale, this._scale - this._opts.scaleStep);
        if (this._scale !== oldScale && this.onScaleChange) {
            this.onScaleChange(this._scale, oldScale);
        }
    }
    _stepUp() {
        const oldScale = this._scale;
        this._scale = Math.min(this._opts.maxScale, this._scale + this._opts.scaleStep);
        if (this._scale !== oldScale && this.onScaleChange) {
            this.onScaleChange(this._scale, oldScale);
        }
    }
    /** Reset to max scale and clear all tracking state */
    reset() {
        this._scale = this._opts.maxScale;
        this._fpsAvg = 0;
        this._lowTime = 0;
        this._highTime = 0;
    }
}
//# sourceMappingURL=adaptive-quality.js.map